import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  UserX, Search, Shield, Clock, Mail, Phone, MapPin, Calendar,
  FileText, ChevronLeft, ChevronRight, Loader2, Eye, AlertTriangle,
  ScrollText, Globe,
} from 'lucide-react';

export default function DeletedAccountsPage() {
  const { user } = useAuth();
  usePageTitle('Silinmis Hesaplar');
  const [archives, setArchives] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const limit = 20;

  const loadArchives = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * limit, limit };
      if (search) params.search = search;
      const r = await api.get('/deleted-accounts', { params });
      setArchives(r.data.archives);
      setTotal(r.data.total);
    } catch {
      toast.error('Silinmis hesaplar yuklenemedi');
    } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { loadArchives(); }, [loadArchives]);

  const loadDetail = async (id) => {
    try {
      const r = await api.get(`/deleted-accounts/${id}`);
      setSelectedArchive(r.data);
      setDetailOpen(true);
    } catch {
      toast.error('Detay yuklenemedi');
    }
  };

  const totalPages = Math.ceil(total / limit);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!['admin', 'kent_konseyi_baskani'].includes(user?.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Bu sayfaya erisim yetkiniz bulunmamaktadir</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="deleted-accounts-page">
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-red-900 via-red-800 to-red-900 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
                <UserX className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Silinmis Hesaplar</h1>
                <p className="text-red-200 text-sm">KVKK kapsaminda 10 yil saklanmaktadir</p>
              </div>
            </div>
            <Badge className="bg-white/10 text-white border-white/20 text-sm">{total} kayit</Badge>
          </div>
        </div>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="E-posta, isim veya kullanici ID ile ara..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
                data-testid="deleted-search"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-red-600" /></div>
      ) : archives.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <UserX className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-lg font-medium text-slate-900">Silinmis hesap bulunamadi</h3>
            <p className="text-sm text-slate-500 mt-1">Henuz kalici olarak silinmis hesap yok</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {archives.map(a => (
              <Card key={a.id} className="shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => loadDetail(a.id)} data-testid={`archive-card-${a.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-sm font-bold">
                        {a.name_masked?.[0] || '?'}{a.surname_masked?.[0] || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{a.name_masked} {a.surname_masked}</p>
                        <p className="text-xs text-slate-400">{a.email_masked}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-xs">{a.role || '-'}</Badge>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Silindi: {formatDate(a.hard_deleted_at)}</p>
                        <p className="text-[10px] text-slate-400">Saklama: {formatDate(a.retention_until)}</p>
                      </div>
                      <Eye className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-8 w-8 p-0"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm text-slate-500">Sayfa {page} / {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-8 w-8 p-0"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Silinmis Hesap Detayi
            </DialogTitle>
          </DialogHeader>
          {selectedArchive && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <InfoItem icon={Mail} label="E-posta (Maskeli)" value={selectedArchive.email_masked} />
                <InfoItem icon={FileText} label="Ad (Maskeli)" value={`${selectedArchive.name_masked || '-'} ${selectedArchive.surname_masked || '-'}`} />
                <InfoItem icon={Phone} label="Telefon (Maskeli)" value={selectedArchive.phone_masked} />
                <InfoItem icon={Shield} label="TC Kimlik (Maskeli)" value={selectedArchive.tc_kimlik_masked} />
                <InfoItem icon={FileText} label="Rol" value={selectedArchive.role} />
                <InfoItem icon={Calendar} label="Hesap Olusturma" value={formatDate(selectedArchive.created_at)} />
                <InfoItem icon={Clock} label="Silme Talebi" value={formatDate(selectedArchive.deletion_requested_at)} />
                <InfoItem icon={UserX} label="Kalici Silme" value={formatDate(selectedArchive.hard_deleted_at)} />
              </div>

              {selectedArchive.ip_addresses?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Globe className="w-4 h-4" /> IP Adresleri</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedArchive.ip_addresses.map((ip, i) => (
                      <Badge key={i} variant="outline" className="text-xs font-mono">{ip}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedArchive.audit_logs?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><ScrollText className="w-4 h-4" /> Islem Gecmisi (Son {selectedArchive.audit_logs.length})</h4>
                  <div className="max-h-48 overflow-y-auto rounded-lg border">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium text-slate-600">Islem</th>
                          <th className="text-left p-2 font-medium text-slate-600">IP</th>
                          <th className="text-left p-2 font-medium text-slate-600">Tarih</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {selectedArchive.audit_logs.map((log, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="p-2 text-slate-700">{log.action}</td>
                            <td className="p-2 text-slate-500 font-mono">{log.ip_address || '-'}</td>
                            <td className="p-2 text-slate-400">{log.created_at ? new Date(log.created_at).toLocaleString('tr-TR') : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Bu kayit KVKK kapsaminda {formatDate(selectedArchive.retention_until)} tarihine kadar saklanacaktir.
                Kisisel veriler maskelenmistir.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-slate-50">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
        <p className="text-sm font-medium text-slate-700">{value || '-'}</p>
      </div>
    </div>
  );
}
